import type { VaultItem, Identifier, Passkey } from './index';

/**
 * The web identifiers an item matches against. Prefers the typed
 * `identifiers` field; falls back to the legacy single `url` string when
 * `identifiers` is absent. Returns an empty array when neither is set, or
 * when `identifiers` is present but contains no web entries — including
 * the deliberately-empty case (user removed every identifier), which must
 * NOT resurrect the legacy `url`.
 */
export function effectiveWebIdentifiers(
  // Widened from `VaultItem` to the four fields this actually reads, so callers
  // holding a partial item — an importer's `ImportedItem`, a dedupe projection —
  // can pass it without inventing `id`/`createdAt`/`updatedAt` it never touches.
  // Purely a widening: every existing caller still type-checks.
  item: Pick<VaultItem, 'type' | 'rpId' | 'identifiers' | 'url'>,
): string[] {
  // Passkey items match the current site via their RP id. Skip the
  // identifiers[] / url branches — those fields are not meaningful for
  // passkeys, even if they happen to be present on the item.
  if (item.type === 'passkey') {
    return item.rpId ? [`https://${item.rpId}`] : [];
  }
  if (item.identifiers) {
    return item.identifiers
      .filter((i): i is Extract<Identifier, { type: 'web' }> => i.type === 'web')
      .map((i) => i.value);
  }
  return item.url ? [item.url] : [];
}

/**
 * The Android-app identifiers (package names) an item matches against.
 * Legacy `url` is never treated as an app identifier — items created by
 * the pre-fix Android save flow have malformed `https://com.x.y` URLs and
 * the operator migration script rewrites them to typed app identifiers.
 */
export function effectiveAppIdentifiers(item: VaultItem): string[] {
  if (!item.identifiers) return [];
  return item.identifiers
    .filter((i): i is Extract<Identifier, { type: 'android-app' }> => i.type === 'android-app')
    .map((i) => i.value);
}

/**
 * True when `url` is a parseable web URL whose hostname equals `rpId` or is
 * a subdomain of `rpId` (registrable-domain-suffix match, per WebAuthn).
 * A passkey whose origin had host H may be used for any assertion with
 * rpId R where H === R or H ends with "." + R.
 *
 * The old implementation stripped a leading "www." from the URL host but
 * not from rpId, which broke the symmetric case (host www.foo / rpId
 * www.foo) and missed subdomains beyond www.* (host app.foo / rpId foo).
 */
export function urlMatchesRpId(url: string, rpId: string): boolean {
  try {
    const host = new URL(url).hostname.toLowerCase();
    const rp = rpId.toLowerCase();
    return host === rp || host.endsWith('.' + rp);
  } catch { return false; }
}

/**
 * All passkeys associated with this item. Returns the embedded `passkeys[]`
 * for login items, or the singular passkey fields wrapped into an array
 * for standalone passkey items. [] when neither applies.
 */
export function effectivePasskeys(item: VaultItem): Passkey[] {
  if (item.type === 'login') {
    return item.passkeys ?? [];
  }
  // Passkey-type items have all these fields as optional on VaultItem
  // (the type is a flat union, not discriminated), so narrow defensively.
  if (item.type === 'passkey' && item.credentialId && item.publicKeyJwk && item.privateKeyJwk
      && item.userHandle !== undefined && item.userName !== undefined
      && item.userDisplayName !== undefined && item.algorithm !== undefined) {
    return [{
      credentialId: item.credentialId,
      publicKeyJwk: item.publicKeyJwk,
      privateKeyJwk: item.privateKeyJwk,
      userHandle: item.userHandle,
      userName: item.userName,
      userDisplayName: item.userDisplayName,
      algorithm: item.algorithm,
      createdAt: item.createdAt,
      lastUsedAt: item.lastUsedAt,
    }];
  }
  return [];
}
